import React, {
    useCallback,
    useEffect,
    useState,
    useMemo,
    useRef
} from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import CircularProgress from '@material-ui/core/CircularProgress';

// Actions
import * as MediaLibraryActions from 'redux/actions/mediaLibrary';

// Selectors
import { getMediaLibrary, getUser } from 'redux/selectors';

// Utils
import { closeDialog } from 'common/utils/dialog-utils';

// Components
import ImageDetailsDialog from './ImageDetailsDialog';

// File Icons
import iconPdf from 'assets/images/icons_svg/icon-pdf.svg';
import iconCsv from 'assets/images/icons_svg/icon-csv.svg';
import iconDoc from 'assets/images/icons_svg/icon-doc.svg';
import iconDocx from 'assets/images/icons_svg/icon-docx.svg';
import iconFile from 'assets/images/icons_svg/icon-file.svg';
import iconMov from 'assets/images/icons_svg/icon-mov.svg';
import iconMp3 from 'assets/images/icons_svg/icon-mp3.svg';
import iconMp4 from 'assets/images/icons_svg/icon-mp4.svg';
import iconXls from 'assets/images/icons_svg/icon-xls.svg';
import iconCloseSm from 'assets/images/icons_svg/icon_close-sm.svg';

const THUMBNAIL_SIZES = {
    S: 'small',
    M: 'medium',
    L: 'large'
};

const GROUP_BY_OPTIONS = [
    { value: 'day', label: 'Day' },
    { value: 'month', label: 'Month' },
    { value: 'year', label: 'Year' }
];

const ORDER_OPTIONS = [
    { value: 'newest', label: 'Newest to Oldest' },
    { value: 'oldest', label: 'Oldest to Newest' }
];

const fileIcons = {
    csv: iconCsv,
    doc: iconDoc,
    docx: iconDocx,
    mov: iconMov,
    mp3: iconMp3,
    mp4: iconMp4,
    pdf: iconPdf,
    xls: iconXls,
    default: iconFile
};

const getFileIcon = (fileName) => {
    if (!fileName) {
        return iconFile;
    }
    const ext = fileName.split('.').pop()?.toLowerCase();
    return fileIcons[ext] || fileIcons.default;
};

const MediaLibraryDialog = ({
    dispatch,
    mediaLibrary,
    user,
    onSelectImage
}) => {
    const [filters, setFilters] = useState({
        groupBy: 'month',
        thumbnailSize: 'M',
        order: 'newest'
    });
    const [detailsImage, setDetailsImage] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const isInitialMount = useRef(true);
    const loadMoreRef = useRef(null);
    const observerRef = useRef(null);

    // Fetch media on mount
    useEffect(() => {
        dispatch(
            MediaLibraryActions.fetch({
                group_by: filters.groupBy,
                order: filters.order,
                page: 1
            })
        );

        // Cleanup on unmount
        return () => {
            dispatch(MediaLibraryActions.reset());
        };
    }, [dispatch, filters.groupBy, filters.order]);

    // Refetch when filters change (skip initial mount)
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        dispatch(MediaLibraryActions.reset());
        dispatch(
            MediaLibraryActions.fetch({
                group_by: filters.groupBy,
                order: filters.order,
                page: 1
            })
        );
    }, [dispatch, filters.groupBy, filters.order]);

    const handleFilterChange = useCallback((key, value) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value
        }));
    }, []);

    const handleLoadMore = useCallback(() => {
        if (mediaLibrary.hasNext && !mediaLibrary.loading) {
            dispatch(
                MediaLibraryActions.fetch({
                    group_by: filters.groupBy,
                    order: filters.order,
                    page: mediaLibrary.page + 1
                })
            );
        }
    }, [dispatch, filters, mediaLibrary]);

    useEffect(() => {
        if (!loadMoreRef.current) {
            return;
        }

        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        observerRef.current = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry.isIntersecting) {
                    handleLoadMore();
                }
            },
            {
                root: null,
                rootMargin: '200px',
                threshold: 0.1
            }
        );

        observerRef.current.observe(loadMoreRef.current);

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [handleLoadMore, mediaLibrary.hasNext, mediaLibrary.loading]);

    const handleImageClick = useCallback(
        (image) => {
            if (onSelectImage) {
                onSelectImage(image);
                closeDialog();
            } else {
                // Open image details dialog
                setDetailsImage(image);
                setDetailsOpen(true);
            }
        },
        [onSelectImage]
    );

    // Get all images as a flat array for navigation
    const allImages = useMemo(() => {
        return mediaLibrary.groups.flatMap((group) => group.media);
    }, [mediaLibrary.groups]);

    // Get current image index for navigation
    const currentImageIndex = useMemo(() => {
        if (!detailsImage) {
            return -1;
        }
        return allImages.findIndex((img) => img.id === detailsImage.id);
    }, [allImages, detailsImage]);

    const handleDetailsPrev = useCallback(() => {
        if (currentImageIndex > 0) {
            setDetailsImage(allImages[currentImageIndex - 1]);
        }
    }, [allImages, currentImageIndex]);

    const handleDetailsNext = useCallback(() => {
        if (currentImageIndex < allImages.length - 1) {
            setDetailsImage(allImages[currentImageIndex + 1]);
        }
    }, [allImages, currentImageIndex]);

    const handleDetailsClose = useCallback(() => {
        setDetailsOpen(false);
        setDetailsImage(null);
    }, []);

    const gridSizeClass = useMemo(() => {
        return `media-library__grid--${THUMBNAIL_SIZES[filters.thumbnailSize]}`;
    }, [filters.thumbnailSize]);

    const getImageSrc = useCallback(
        (media) => {
            if (media.file_type_thumbnail) {
                return media.file_type_thumbnail;
            }
            // Use appropriate size based on thumbnail setting
            switch (filters.thumbnailSize) {
                case 'S':
                    return (
                        media.xs_image ||
                        media.md_xs_image ||
                        media.url ||
                        media.src ||
                        media.image
                    );
                case 'L':
                    return (
                        media.lg_image ||
                        media.xl_image ||
                        media.url ||
                        media.src ||
                        media.image
                    );
                case 'M':
                default:
                    return (
                        media.md_xs_image ||
                        media.xs_image ||
                        media.url ||
                        media.src ||
                        media.image
                    );
            }
        },
        [filters.thumbnailSize]
    );

    const renderFilters = () => (
        <div className="media-library__filters-panel">
            <div className="media-library__filters-title">Filters</div>
            <div className="media-library__filters">
                <div className="media-library__filter-group">
                    <span className="media-library__filter-label">
                        View Options
                    </span>
                    <select
                        className="media-library__filter-select"
                        value={filters.groupBy}
                        onChange={(e) =>
                            handleFilterChange('groupBy', e.target.value)
                        }
                    >
                        {GROUP_BY_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="media-library__filter-group">
                    <span className="media-library__filter-label">
                        Thumbnail Size
                    </span>
                    <select
                        className="media-library__filter-select"
                        value={THUMBNAIL_SIZES[filters.thumbnailSize]}
                        onChange={(e) => {
                            const selected = Object.keys(THUMBNAIL_SIZES).find(
                                (key) => THUMBNAIL_SIZES[key] === e.target.value
                            );
                            handleFilterChange(
                                'thumbnailSize',
                                selected || 'M'
                            );
                        }}
                    >
                        {Object.entries(THUMBNAIL_SIZES).map(([key, value]) => (
                            <option key={key} value={value}>
                                {value.charAt(0).toUpperCase() + value.slice(1)}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="media-library__filter-group">
                    <span className="media-library__filter-label">Order</span>
                    <select
                        className="media-library__filter-select"
                        value={filters.order}
                        onChange={(e) =>
                            handleFilterChange('order', e.target.value)
                        }
                    >
                        {ORDER_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );

    const renderImages = () => {
        if (mediaLibrary.loading && mediaLibrary.groups.length === 0) {
            return (
                <div className="media-library__loading">
                    <CircularProgress />
                </div>
            );
        }

        if (!mediaLibrary.loading && mediaLibrary.groups.length === 0) {
            return (
                <div className="media-library__empty">
                    <svg
                        className="media-library__empty-icon"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                    </svg>
                    <p className="media-library__empty-text">
                        No media available
                    </p>
                    <p className="media-library__empty-subtext">
                        Media from your journal entries will appear here
                    </p>
                </div>
            );
        }

        return (
            <>
                {mediaLibrary.groups.map((group) => {
                    const imageMedia = group.media.filter((media) =>
                        /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(
                            media.file_name
                        )
                    );
                    if (imageMedia.length === 0) {
                        return null;
                    }
                    return (
                        <div key={group.key} className="media-library__group">
                            <span className="media-library__filter-label">
                                {group.label}
                            </span>
                            <div
                                className={`media-library__grid ${gridSizeClass}`}
                            >
                                {imageMedia.map((media) => {
                                    const hasImage =
                                        media.image ||
                                        media.xs_image ||
                                        media.md_xs_image ||
                                        media.url ||
                                        media.src;
                                    const imageSrc = getImageSrc(media);
                                    return (
                                        <div
                                            key={media.id}
                                            className="media-library__image-wrapper"
                                            onClick={() =>
                                                handleImageClick(media)
                                            }
                                        >
                                            {hasImage ? (
                                                <img
                                                    className="media-library__image"
                                                    src={imageSrc}
                                                    alt={
                                                        media.file_name ||
                                                        'Entry image'
                                                    }
                                                    loading="lazy"
                                                />
                                            ) : (
                                                <div className="media-library__file-wrapper">
                                                    <img
                                                        src={getFileIcon(
                                                            media.file_name
                                                        )}
                                                        alt={
                                                            media.file_name ||
                                                            'file'
                                                        }
                                                        className="media-library__file-icon"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}

                {mediaLibrary.hasNext && (
                    <div className="media-library__load-more">
                        <button
                            className="media-library__load-more-btn"
                            onClick={handleLoadMore}
                            disabled={mediaLibrary.loading}
                        >
                            {mediaLibrary.loading ? 'Loading...' : 'Load More'}
                        </button>
                    </div>
                )}
                <div ref={loadMoreRef} />
            </>
        );
    };

    return (
        <div id="media-library-modal" className="modal-frame">
            <div className="modal-box modal-box--gallery modal-box--media-library">
                <div className="modal__header">
                    <span>Media Library</span>
                    {onSelectImage && (
                        <button
                            type="button"
                            className="modal__close btn-icon btn-icon--3"
                            onClick={(e) => {
                                e.stopPropagation();
                                closeDialog();
                            }}
                        >
                            <img src={iconCloseSm} alt="" />
                        </button>
                    )}
                </div>
                <div className="modal__body media-library__body media-library__layout">
                    <div className="media-library__content">
                        {renderImages()}
                    </div>
                    <aside className="media-library__sidebar">
                        {renderFilters()}
                    </aside>
                </div>
            </div>

            <ImageDetailsDialog
                open={detailsOpen}
                onClose={handleDetailsClose}
                image={detailsImage}
                onPrev={handleDetailsPrev}
                onNext={handleDetailsNext}
                hasPrev={currentImageIndex > 0}
                hasNext={currentImageIndex < allImages.length - 1}
            />
        </div>
    );
};

const mapStateToProps = createStructuredSelector({
    mediaLibrary: getMediaLibrary,
    user: getUser
});

export default connect(mapStateToProps)(MediaLibraryDialog);
